import useSWR, { KeyedMutator, SWRResponse } from 'swr';
import { swrFetcher, authBlobFetcher } from 'packages/lib/utils/fetch';
import {
  patch,
  post,
  remove,
} from 'packages/lib/utils/legacy-tsapi/rest-helpers';
import {
  isNonNullObject,
  isNullableString,
  isNumber,
  NullableString,
} from 'packages/lib/common-types/types';
import type { SirenCategoryName } from 'packages/main/components/audio-tool/audio-tool-types';
import { isArrayOf } from 'packages/lib/common-types/types';
import useSWRResponseToRecoil, {
  SWRToRecoilResponse,
} from 'packages/lib/utils/hooks/use-swr-response-to-recoil';
import {
  audioExtractionAPIResultAtom,
  categoriesAPILoadingAtom,
  categoriesAPIResultsAtom,
} from 'packages/main/components/audio-tool/atoms';
import { isTask, type Task } from 'packages/lib/api/annotations/contract/task';
import {
  CategoriesResp,
  categoriesZod,
} from 'packages/lib/api/annotations/contract/category';
import { UnexpectedResponseType } from 'packages/lib/utils/error';

// Per Edvin: For now, there is only one Instruction for Audio Annotations: 'audio-_v2023-1'
// Its matching token can be hard-coded until there is more than one.
// https://ml-staging.motional.com/instructions
const AUDIO_INSTRUCTION_TOKEN = '6aca58be4d024bcf85a094b3d0e32bdc';

export class AudioAnnotation {
  constructor(
    audioExtractionToken: string,
    startTimestamp: number, // UNIX timestamp (microseconds since epoch)
    endTimestamp: number, // UNIX timestamp (microseconds since epoch)
    sirenCategoryName: SirenCategoryName,
    categoryToken: string,
    token?: string
  ) {
    this.audioExtractionToken = audioExtractionToken;
    this.startTimestamp = startTimestamp;
    this.endTimestamp = endTimestamp;
    this.categoryToken = categoryToken;
    this.instructionToken = AUDIO_INSTRUCTION_TOKEN;
    this.token = token;
  }

  audioExtractionToken: NullableString;
  startTimestamp: number;
  endTimestamp: number;
  token?: NullableString;
  instructionToken: NullableString;
  categoryToken: NullableString;
}

// An AudioAnnotation is a single starting & ending region annotated as a specific type of siren (Horn, Wail, etc.) for a given AudioExtraction
// There can be many (potentially overlapping) AudioAnnotations per AudioExtraction
export const isAudioAnnotation = (record: unknown): record is AudioAnnotation =>
  isNonNullObject(record) &&
  isNullableString(record.audioExtractionToken) &&
  isNumber(record.startTimestamp) &&
  isNumber(record.endTimestamp) &&
  isNullableString(record.token) &&
  isNullableString(record.instructionToken) &&
  isNullableString(record.categoryToken);

// token is the audio-annotation token
export const useAudioAnnotationSWR = (
  token?: string
): SWRResponse<AudioAnnotation, UnexpectedResponseType> => {
  return useSWR<AudioAnnotation>(
    token && token.trim().length > 0 ? `/audio-annotations/${token}` : null,
    (url: string) =>
      swrFetcher<AudioAnnotation>(url, {
        baseURL: 'annotations',
        typeGuard: isAudioAnnotation,
      })
  );
};

export interface AudioExtraction {
  token: NullableString;
  audioRate: number;
  audioChannels: number;
  bitDepth: number;
  startTimestamp: number; // UNIX timestamp (microseconds since epoch)
  endTimestamp: number; // UNIX timestamp (microseconds since epoch)
  filename: NullableString;
  sensorToken: NullableString;
  lidarExtractionToken: NullableString;
}

export const isAudioExtraction = (record: unknown): record is AudioExtraction =>
  isNonNullObject(record) &&
  isNullableString(record.token) &&
  isNumber(record.audioRate) &&
  isNumber(record.audioChannels) &&
  isNumber(record.bitDepth) &&
  isNumber(record.startTimestamp) &&
  isNumber(record.endTimestamp) &&
  isNullableString(record.filename) &&
  isNullableString(record.sensorToken) &&
  isNullableString(record.lidarExtractionToken);

// An extraction is a sound clip (typically 20 seconds long, 4 channels) at a specific timestamp of the log
// This API returns metadata about the extraction
export const useAudioExtractionSWRToRecoil = (
  token?: string
): SWRToRecoilResponse<AudioExtraction> => {
  const { data, error, isValidating } = useSWR<AudioExtraction>(
    token && token.trim().length > 0 ? `/audio-extractions/${token}` : null,
    (url: string) =>
      swrFetcher<AudioExtraction>(url, {
        baseURL: 'annotations',
        typeGuard: isAudioExtraction,
      })
  );

  return useSWRResponseToRecoil(
    {
      data: data ?? null,
      error: error ?? null,
      isLoading: isValidating,
    },
    {
      data: audioExtractionAPIResultAtom,
      loading: categoriesAPILoadingAtom,
    }
  );
};

// Get .acc audio file data as a blob
// Preferable in future to switch to instead getting an S3 file link instead? (by using header 'X-Direct-Access': 'yes')
export const useAudioExtractionContentSWR = (
  token?: string
): SWRResponse<Blob, UnexpectedResponseType> => {
  return useSWR<Blob>(
    token && token.trim().length > 0
      ? `/audio-extractions/${token}/content`
      : null,
    (url: string) =>
      authBlobFetcher(url, {
        baseURL: 'annotations',
      })
  );
};

export const addUpdateAudioAnnotation = async (
  audioAnnotation: Partial<AudioAnnotation>
): Promise<string> => {
  const { token } = audioAnnotation;
  if (token) {
    await patch('annotations', `/audio-annotations/${token}`, {
      startTimestamp: audioAnnotation.startTimestamp,
      endTimestamp: audioAnnotation.endTimestamp,
    });
    return token;
  }
  // token will be created and added by backend
  const [rbody] = await post(
    'annotations',
    `/audio-annotations`,
    audioAnnotation
  );
  return rbody.token;
};

/** @returns Promise with true/false indicating success/failure */
export const deleteAudioAnnotation = async (
  token: string
): Promise<Response> => {
  const [, resp] = await remove('annotations', `/audio-annotations/${token}`);
  return resp;
};

export const isAudioAnnotationsResponse = (
  record: unknown
): record is AudioAnnotation[] =>
  isNonNullObject(record) &&
  (record === null ||
    (Array.isArray(record) && record.every((item) => isAudioAnnotation(item))));

// Get a list of ALL audio annotations for ALL extractions
export const useAudioAnnotationsSWR = (): SWRResponse<
  AudioAnnotation[],
  UnexpectedResponseType
> => {
  return useSWR<AudioAnnotation[]>(`/audio-annotations`, (url: string) =>
    swrFetcher<AudioAnnotation[]>(url, {
      baseURL: 'annotations',
      typeGuard: isAudioAnnotationsResponse,
    })
  );
};

// Get a list of all Audio Annotations for a specific Audio Extraction
export const useAudioAnnotationsForExtractionSWR = (
  audioExtractionToken?: string
): SWRResponse<AudioAnnotation[], UnexpectedResponseType> => {
  return useSWR<AudioAnnotation[]>(
    audioExtractionToken && audioExtractionToken.trim().length > 0
      ? `/audio-annotations?audioExtractionToken=${audioExtractionToken}`
      : null,
    (url: string) =>
      swrFetcher<AudioAnnotation[]>(url, {
        baseURL: 'annotations',
        typeGuard: isAudioAnnotationsResponse,
      })
  );
};

export const useCategoriesSWRToRecoil =
  (): SWRToRecoilResponse<CategoriesResp> => {
    const { data, error, isValidating } = useSWR<CategoriesResp, Error>(
      '/categories',
      (url: string) =>
        swrFetcher(url, {
          baseURL: 'annotations',
          zodParse: categoriesZod.parse,
        })
    );

    return useSWRResponseToRecoil(
      {
        data: data ?? null,
        error: error ?? null,
        isLoading: isValidating,
      },
      {
        data: categoriesAPIResultsAtom,
        loading: categoriesAPILoadingAtom,
      }
    );
  };

interface UseAudioTaskReturnType {
  data: Task | null;
  isValidating: boolean;
  error: Error | undefined;
  mutate: KeyedMutator<Task[]>;
}

export const useAudioTaskSWR = (
  audioExtractionToken?: string
): UseAudioTaskReturnType => {
  const { data, error, mutate, isValidating } = useSWR<Task[]>(
    audioExtractionToken
      ? `/tasks?audioExtractionToken=${audioExtractionToken}&orderBy=-createdAt`
      : null,
    (url: string) =>
      swrFetcher<Task[]>(url, {
        baseURL: 'annotations',
        typeGuard: isArrayOf(isTask),
      }),
    { revalidateOnFocus: false }
  );

  return { data: data ? data[0] : null, error, mutate, isValidating };
};
